import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

// Redux
import { connect } from 'react-redux';
import { setAlert } from '../../../redux/actions/alertActions';

// Server
import { listAlerts, downloadAlertsCsv } from '../../../api';

// Components
import PageHeader from '../../../components/PageHeader';
import { PillTrack, Pill } from '../../../components/Pill';
import { ListNote, Pager, PER_PAGE } from '../../../components/ListControls';

// Constants
import { T, FONT_MONO, INDICATOR_TEXT } from '../../../constants/tokens';
import { statusColor, incidentDuration, formatWhen } from './format';

const MODES = [
  { key: 'all', label: 'All' },
  { key: 'real', label: 'Real' },
  { key: 'drills', label: 'Drills' }
];

// The statuses an alert can be. Choosing one narrows the list; choosing it
// again clears it.
const STATUSES = ['Emergency', 'Hold', 'Medical', 'Admin'];

const REFRESH_MS = 15000;

const useStyles = makeStyles({
  card: {
    display: 'grid',
    gridTemplateColumns: '12px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    padding: '16px 20px',
    marginBottom: 8,
    background: T.elevated,
    border: '1px solid ' + T.hairline,
    borderRadius: 6,
    textAlign: 'left',
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': { background: T.surfaceSoft, borderColor: T.muted },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  },
  button: {
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    height: 40,
    padding: '0 20px',
    borderRadius: 6,
    border: '1px solid ' + T.hairline,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover:not(:disabled)': { background: T.surfaceSoft },
    '&:disabled': { color: T.mutedSoft, cursor: 'default' }
  }
});

const eyebrow = { fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body };

/**
 * Alert history: every alert the school has raised, newest first.
 *
 * Filters by real or drill and by status, pages ten at a time, and exports the
 * filtered history as CSV. Each alert opens its detail page.
 */
function AlertHistory({ windowWidth, setError }) {
  const classes = useStyles();
  const history = useHistory();
  const [mode, setMode] = useState('all');
  const [status, setStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(() => {
    listAlerts({ mode, status, limit: PER_PAGE, offset: (page - 1) * PER_PAGE })
      .then((data) => {
        if (mounted.current) setResult({ ...data, mode, status, page });
      })
      .catch((error) => setError(error ? error : 'Could not retrieve the alert history.'));
  }, [mode, status, page, setError]);

  useEffect(() => {
    mounted.current = true;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [load]);

  // A result for an older filter is not this filter's, so show loading instead.
  const current = result && result.mode === mode && result.status === status && result.page === page ? result : null;
  const pageCount = current ? Math.max(1, Math.ceil(current.total / PER_PAGE)) : 1;
  const narrow = windowWidth < 700;

  // Any filter change starts again from the first page.
  const chooseMode = (next) => {
    setMode(next);
    setPage(1);
  };
  const chooseStatus = (next) => {
    setStatus(status === next ? null : next);
    setPage(1);
  };

  const exportCsv = () => {
    setExporting(true);
    downloadAlertsCsv({ mode, status })
      .catch((error) => setError(error ? error : 'Could not export the alert history.'))
      .finally(() => mounted.current && setExporting(false));
  };

  return (
    <>
      <PageHeader
        title='Alert history'
        subtitle={current ? (current.total === 1 ? '1 alert' : current.total + ' alerts') : 'Loading…'}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <PillTrack>
          {MODES.map((m) => (
            <Pill key={m.key} on={mode === m.key} onClick={() => chooseMode(m.key)}>{m.label}</Pill>
          ))}
        </PillTrack>
        <PillTrack scroller style={{ maxWidth: '100%' }}>
          {STATUSES.map((s) => (
            <Pill key={s} on={status === s} onClick={() => chooseStatus(s)}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: 9999, background: statusColor(s) }} />{s}
              </span>
            </Pill>
          ))}
        </PillTrack>
        <div style={{ flex: '1 0 12px' }} />
        <button type='button' className={classes.button} onClick={exportCsv} disabled={exporting || !current || current.total === 0}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {!current && <ListNote>Loading…</ListNote>}
      {current && current.alerts.length === 0 &&
        <ListNote>{mode === 'all' && !status ? 'No alerts recorded yet.' : 'No alerts recorded for this filter.'}</ListNote>}

      {current && current.alerts.map((incident) => (
        <button
          type='button'
          key={incident.getID()}
          className={classes.card}
          onClick={() => history.push('/dashboard/alerts/' + incident.getID())}
          style={incident.getIsActive() ? { borderColor: statusColor(incident.getStatus()) } : undefined}
        >
          <span style={{ width: 12, height: 12, borderRadius: 9999, background: statusColor(incident.getStatus()) }} />

          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, lineHeight: '24px', color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {incident.getTitle()}
            </div>
            <div style={{ fontSize: 14, lineHeight: '20px', color: T.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: narrow ? 'normal' : 'nowrap' }}>
              {[formatWhen(incident.getStartedAt()), incident.getSource(), incident.getRaisedBy()].filter(Boolean).join(' · ')}
            </div>
            {narrow &&
              <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.body, marginTop: 4 }}>
                {incidentDuration(incident)}
              </div>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {!narrow &&
              <div style={{ textAlign: 'right' }}>
                <div style={eyebrow}>Duration</div>
                <div style={{ fontFamily: FONT_MONO, fontSize: 14, color: incident.getIsActive() ? INDICATOR_TEXT.error : T.ink, whiteSpace: 'nowrap' }}>
                  {incidentDuration(incident)}
                </div>
              </div>}
            <span
              style={{
                background: incident.getIsDrill() ? T.hairlineSoft : T.ink,
                color: incident.getIsDrill() ? T.ink : T.canvas,
                borderRadius: 9999,
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '16px',
                whiteSpace: 'nowrap'
              }}
            >
              {incident.getIsDrill() ? 'Drill' : 'Real event'}
            </span>
          </div>
        </button>
      ))}

      <Pager page={page} pageCount={pageCount} onChange={setPage} />
    </>
  );
}

AlertHistory.propTypes = {
  windowWidth: PropTypes.number,
  setError: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  windowWidth: state.ui.windowSize.width
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg)
});

export default connect(mapStateToProps, mapDispatchToProps)(AlertHistory);
